// @flow
import invariant from 'invariant';
import * as React from 'react';
import { connect } from 'react-redux';
import { moveArrayItem } from 'utils/functional';
import Box from 'components/Box';
import Video from 'components/Video';
import ImagePicker from 'components/ImagePicker';
import styled from 'styled-components';
import type { ImageData } from 'api/shared';
import { imageUrl } from 'utils/filestack';
import { retinaBackgroundImage } from 'utils/style';
import Container from 'components/Container';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { colors } from '../theme';
import { showModal } from '../redux/main/actions/modal';

const ImageContainer = styled.div.attrs({ role: 'button' })`
  position: relative;
  width: 150px;
  height: 150px;
  background-color: ${colors.battleshipGrey};
  ${props =>
    retinaBackgroundImage(
      imageUrl({
        handle: props.handle,
        transform: `resize=width:150,height:150,fit:crop,align:center`
      }),
      {
        filename2x: imageUrl({
          handle: props.handle,
          transform: `resize=width:300,height:300,fit:crop,align:center`
        })
      }
    )}
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  cursor: move;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  z-index: 20;
  top: -13px;
  right: -13px;
  width: 25px;
  height: 25px;
  background-color: ${colors.waterBlue};
  background-image: url('/static/images/icons/trashcan-white.svg');
  background-size: 9px 10px;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease-in;
  &:hover {
    background-color: ${colors.turtleGreen};
  }
`;

const SortableImage = SortableElement(
  ({ image, imageIndex, removeImage, qaId }) => {
    const removeHandler = () => {
      removeImage(imageIndex);
    };
    return (
      <Container m={11}>
        <ImageContainer
          data-qa-id={`${qaId}-image-uploaded-image`}
          handle={image.handle}
        >
          {image.type && image.type === 'VIDEO' && (
            <Video url={image.videoUrl} noPlayer fill smallButton />
          )}
          <DeleteButton
            title={getText('Remove')}
            data-qa-id={`${qaId}-remove-image`}
            onClick={removeHandler}
          />
        </ImageContainer>
      </Container>
    );
  }
);

const SortableGrid = SortableContainer(
  ({ values, removeImage, editImage, qaId, children }) => (
    <Box
      classes={['flex']}
      flexDirection={['column', 'row', null, null]}
      flexWrap={['nowrap', 'wrap', null, null]}
      m={-11}
    >
      {values.map((image, index) => (
        <SortableImage
          image={image}
          removeImage={removeImage}
          editImage={editImage}
          qaId={qaId}
          imageIndex={index}
          index={index}
          key={image.fileId}
        />
      ))}
      {children}
    </Box>
  )
);

type OwnProps = {
  values: ?Array<ImageData>,
  onChange: (Array<ImageData>) => void,
  allowVideo?: boolean,
  id?: string,
  'data-qa-id': string
};

type Props = {
  ...$Exact<OwnProps>,
  showModal: typeof showModal
};

class GalleryPicker extends React.Component<Props> {
  addImage = (image: ?ImageData) => {
    if (image && this.props.values) {
      const values = [...this.props.values];
      values.push(image);
      this.props.onChange(values);
    }
  };

  removeImage = (index: number) => {
    invariant(this.props.values, 'Missing image to remove');
    const values = [...this.props.values];
    values.splice(index, 1);
    this.props.onChange(values);
  };

  sortHandler = ({
    oldIndex,
    newIndex
  }: {
    oldIndex: number,
    newIndex: number
  }) => {
    this.props.onChange(moveArrayItem(this.props.values, oldIndex, newIndex));
  };

  render() {
    const qaId = this.props['data-qa-id'];
    const values = this.props.values || [];

    return (
      <>
        <SortableGrid
          values={values}
          removeImage={this.removeImage}
          qaId={qaId}
          axis="xy"
          onSortEnd={this.sortHandler}
        >
          <Box m={11}>
            <ImagePicker
              image={null}
              data-qa-id={`${qaId}-image`}
              name={qaId}
              buttonOnly
              onChange={this.addImage}
              previewWidth={150}
              previewHeight={150}
            />
          </Box>
        </SortableGrid>
      </>
    );
  }
}

export default connect<Props, OwnProps, _, _, _, _>(
  null,
  { showModal }
)(GalleryPicker);
